export function base64UrlToArrayBuffer(value: string): ArrayBuffer {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "="
  );
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1)
    binary += String.fromCharCode(bytes[i]);
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

type AnyOptions = { publicKey?: Record<string, unknown> } & Record<
  string,
  unknown
>;

function unwrap(options: AnyOptions): Record<string, unknown> {
  return (options.publicKey as Record<string, unknown>) ?? options;
}

export function toCredentialCreationOptions(
  options: AnyOptions
): CredentialCreationOptions {
  const pk = unwrap(options) as Record<string, any>;
  return {
    publicKey: {
      ...pk,
      challenge: base64UrlToArrayBuffer(pk.challenge),
      user: { ...pk.user, id: base64UrlToArrayBuffer(pk.user.id) },
      excludeCredentials: (pk.excludeCredentials ?? []).map((c: any) => ({
        ...c,
        id: base64UrlToArrayBuffer(c.id),
      })),
    } as PublicKeyCredentialCreationOptions,
  };
}

export function toCredentialRequestOptions(
  options: AnyOptions
): CredentialRequestOptions {
  const pk = unwrap(options) as Record<string, any>;
  return {
    publicKey: {
      ...pk,
      challenge: base64UrlToArrayBuffer(pk.challenge),
      allowCredentials: (pk.allowCredentials ?? []).map((c: any) => ({
        ...c,
        id: base64UrlToArrayBuffer(c.id),
      })),
    } as PublicKeyCredentialRequestOptions,
  };
}

export function serializeRegistrationCredential(
  credential: PublicKeyCredential
): string {
  const response = credential.response as AuthenticatorAttestationResponse;
  const getTransports = (response as any).getTransports;
  return JSON.stringify({
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      attestationObject: arrayBufferToBase64Url(response.attestationObject),
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      transports:
        typeof getTransports === "function" ? getTransports.call(response) : [],
    },
  });
}

export function serializeAuthenticationCredential(
  credential: PublicKeyCredential
): string {
  const response = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle
        ? arrayBufferToBase64Url(response.userHandle)
        : null,
    },
  });
}

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    !!navigator.credentials
  );
}
